
import { useState } from "react";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type Entry = Database["public"]["Tables"]["entries"]["Row"];

export const useEntryActions = () => {
  const [loading, setLoading] = useState(false);

  const toggleFavorite = async (entry: Entry) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from("entries")
        .update({ is_favorite: !entry.is_favorite })
        .eq("id", entry.id);

      if (error) throw error;
      
      toast.success(
        entry.is_favorite 
          ? "Removed from favorites" 
          : "Added to favorites"
      );
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error("Failed to update favorite status");
    } finally {
      setLoading(false);
    }
  };

  const togglePinned = async (entry: Entry) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from("entries")
        .update({ is_pinned: !entry.is_pinned })
        .eq("id", entry.id);

      if (error) throw error;
      
      toast.success(
        entry.is_pinned 
          ? "Unpinned from dashboard" 
          : "Pinned to dashboard"
      );
    } catch (error) {
      console.error("Error toggling pinned:", error);
      toast.error("Failed to update pinned status");
    } finally {
      setLoading(false);
    }
  };

  const copyShareLink = async (entry: Entry) => {
    const shareUrl = `${window.location.origin}/shared/${entry.share_id}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied to clipboard");
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      toast.error("Failed to copy share link");
    }
  };

  const stripHtmlTags = (html: string | null): string => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "");
  };

  return {
    toggleFavorite,
    togglePinned,
    copyShareLink,
    stripHtmlTags,
    loading
  };
};
